import { cloneElement, ReactElement } from 'react';

import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

type TabButtonSize = 'sm' | 'md';

// 'inline' lays the icon and label out on one line; 'stacked' centers the icon
// above the label so each tab can also carry a `description` line.
type TabButtonVariant = 'inline' | 'stacked';

export type TabButtonOption<T extends string> = {
  value: T;
  label: string;
  // Only rendered by the 'stacked' variant — an inline tab is one line tall and
  // has nowhere to put it.
  description?: string;
  icon?: ReactElement<IconProps>;
  dataTestid?: string;
  isDanger?: boolean;
};

export type TabButtonProps<T extends string> = {
  options: TabButtonOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: TabButtonSize;
  fullWidth?: boolean;
  variant?: TabButtonVariant;
};

const ICON_SIZE: Record<TabButtonVariant, Record<TabButtonSize, number>> = {
  inline: { sm: 16, md: 24 },
  stacked: { sm: 20, md: 24 },
};

export function TabButton<T extends string>({
  options,
  value,
  onChange,
  size = 'sm',
  fullWidth = false,
  variant = 'inline',
}: TabButtonProps<T>) {
  return (
    <Group $size={size} $fullWidth={fullWidth} $variant={variant}>
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <Tab
            key={option.value}
            type="button"
            aria-pressed={isActive}
            data-testid={option.dataTestid}
            data-danger={option.isDanger ? 'true' : undefined}
            $size={size}
            $fullWidth={fullWidth}
            $variant={variant}
            onClick={() => !isActive && onChange(option.value)}
          >
            {option.icon && cloneElement(option.icon, { size: ICON_SIZE[variant][size] })}
            {option.label}
            {variant === 'stacked' && option.description && (
              <Description>{option.description}</Description>
            )}
          </Tab>
        );
      })}
    </Group>
  );
}

const Group = styled.div<{
  $size: TabButtonSize;
  $fullWidth: boolean;
  $variant: TabButtonVariant;
}>((p) => ({
  display: p.$fullWidth ? 'flex' : 'inline-flex',
  width: p.$fullWidth ? '100%' : undefined,
  // Stacked tabs vary in height when only some of them carry a description, and
  // a short tab centered against a taller neighbour reads as misaligned.
  alignItems: p.$variant === 'stacked' ? 'stretch' : 'center',
  justifyContent: 'center',
  gap: pxToRem(2),
  padding: p.$size === 'md' ? pxToRem(4) : pxToRem(2),
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
}));

const Description = styled.span({
  font: theme.font.regular12,
  color: theme.color.textHelper,
});

const Tab = styled.button<{
  $size: TabButtonSize;
  $fullWidth: boolean;
  $variant: TabButtonVariant;
}>((p) => ({
  display: 'flex',
  flex: p.$fullWidth ? 1 : undefined,
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  borderRadius: pxToRem(6),
  background: 'transparent',
  cursor: 'pointer',
  font: theme.font.semibold14,
  whiteSpace: 'nowrap',
  transition: transitionStandard('color', 'background'),

  ...(p.$variant === 'stacked'
    ? {
        flexDirection: 'column',
        gap: pxToRem(2),
        padding: `${pxToRem(8)} ${pxToRem(12)}`,
        textAlign: 'center',
        // Darker than the inline variant's resting colour: the label needs to
        // outrank the description sitting directly under it.
        color: theme.color.textSecondary,
      }
    : {
        gap: pxToRem(4),
        height: p.$size === 'md' ? pxToRem(32) : pxToRem(28),
        padding: `0 ${pxToRem(12)} 0 ${pxToRem(8)}`,
        color: theme.color.textHelper,
      }),

  '&[aria-pressed="false"]:hover, &:focus-visible': {
    background: theme.color.uiTrans10,
    color: theme.color.textSecondary,
  },

  '&[aria-pressed="true"]': {
    background: theme.color.backgroundLightDark,
    color: theme.color.elementBlue100,
    cursor: 'default',
  },

  // The description is deliberately dimmer than its label, but on the active tab
  // it joins the label's blue rather than staying grey beneath it.
  [`&[aria-pressed="true"] ${Description}`]: {
    color: 'inherit',
  },

  '&[aria-pressed="true"][data-danger="true"]': {
    color: theme.color.elementRed100,
  },
}));
